import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Percent, 
  AlertTriangle,
  ArrowUpRight,
  Clock,
  RefreshCw,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KPIPage() {
  const { token } = useAuth();
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [chargebackDialog, setChargebackDialog] = useState(false);
  const [chargebackDate, setChargebackDate] = useState('');
  const [chargebackRate, setChargebackRate] = useState('');

  const fetchKPIData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/kpi?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setKpiData(await response.json());
      }
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => {
    fetchKPIData();
  }, [fetchKPIData]);

  const handleChargebackUpdate = async () => {
    try {
      await fetch(`${API_URL}/api/admin/kpi/chargeback?date=${chargebackDate}&rate=${chargebackRate}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setChargebackDialog(false);
      fetchKPIData();
    } catch (error) {
      console.error('Error updating chargeback:', error);
    }
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'accent' }) => (
    <Card className="bg-card border-white/5">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className={`text-3xl font-outfit font-semibold ${
              color === 'green' ? 'text-green-500' : 
              color === 'red' ? 'text-red-500' : 
              color === 'yellow' ? 'text-yellow-500' : ''
            }`}>
              {value}
            </p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            color === 'green' ? 'bg-green-500/10' : 
            color === 'red' ? 'bg-red-500/10' : 
            color === 'yellow' ? 'bg-yellow-500/10' : 'bg-accent/10'
          }`}>
            <Icon className={`h-5 w-5 ${
              color === 'green' ? 'text-green-500' : 
              color === 'red' ? 'text-red-500' : 
              color === 'yellow' ? 'text-yellow-500' : 'text-accent'
            }`} strokeWidth={1.5} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            <span>{Math.abs(trend)}% vs previous period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
      <div className="space-y-6" data-testid="admin-kpi">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-semibold">KPI Metrics</h1>
            <p className="text-muted-foreground">Track key performance indicators</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[150px] bg-transparent border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="icon" onClick={fetchKPIData}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>

            <Dialog open={chargebackDialog} onOpenChange={setChargebackDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  Update Chargeback
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Update Chargeback Rate</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Date (YYYY-MM-DD)</Label>
                    <Input 
                      value={chargebackDate}
                      onChange={(e) => setChargebackDate(e.target.value)}
                      placeholder="2026-01-24"
                      className="bg-transparent border-border"
                    />
                  </div>
                  <div>
                    <Label>Chargeback Rate (%)</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={chargebackRate}
                      onChange={(e) => setChargebackRate(e.target.value)}
                      placeholder="0.5"
                      className="bg-transparent border-border"
                    />
                  </div>
                  <Button onClick={handleChargebackUpdate} className="w-full">
                    Update
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : kpiData ? (
          <>
            {/* Primary KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard 
                title="Total Quotes"
                value={kpiData.aggregates.total_quotes.toLocaleString()}
                subtitle={`${days} day period`}
                icon={TrendingUp}
              />
              <MetricCard 
                title="Deposit Conversion"
                value={`${kpiData.aggregates.deposit_conversion_rate}%`}
                subtitle={`${kpiData.aggregates.total_deposits} deposits`}
                icon={Percent}
                color={kpiData.aggregates.deposit_conversion_rate >= 20 ? 'green' : 'yellow'}
              />
              <MetricCard 
                title="Average Margin"
                value={`$${kpiData.aggregates.avg_margin.toFixed(0)}`}
                subtitle="Per load"
                icon={DollarSign}
                color="green"
              />
              <MetricCard 
                title="Dispatch Success"
                value={`${kpiData.aggregates.dispatch_success_rate}%`}
                subtitle="Of deposits"
                icon={ArrowUpRight}
                color={kpiData.aggregates.dispatch_success_rate >= 80 ? 'green' : 'yellow'}
              />
            </div>

            {/* Risk & Operational KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard 
                title="Refund Rate"
                value={`${kpiData.aggregates.refund_rate}%`}
                subtitle="Of deposits"
                icon={RefreshCw}
                color={kpiData.aggregates.refund_rate <= 5 ? 'green' : 'red'}
              />
              <MetricCard 
                title="Risk Flags"
                value={kpiData.aggregates.total_risk_flags}
                subtitle="Total flagged"
                icon={AlertTriangle}
                color={kpiData.aggregates.total_risk_flags > 50 ? 'yellow' : 'green'}
              />
              <MetricCard 
                title="Avg Ladder Escalation"
                value={kpiData.aggregates.avg_ladder_escalation.toFixed(2)}
                subtitle="Per load"
                icon={TrendingUp}
                color={kpiData.aggregates.avg_ladder_escalation < 1 ? 'green' : 'yellow'}
              />
              <MetricCard 
                title="Chargeback Rate"
                value="Manual Input"
                subtitle="Update via button"
                icon={AlertTriangle}
              />
            </div>

            {/* Daily Trend */}
            <Card className="bg-card border-white/5">
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Daily Metrics Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {kpiData.daily_metrics.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No daily metrics recorded yet. Metrics are captured daily at midnight.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4">Date</th>
                          <th className="text-right py-3 px-4">Quotes</th>
                          <th className="text-right py-3 px-4">Deposits</th>
                          <th className="text-right py-3 px-4">Conversion</th>
                          <th className="text-right py-3 px-4">Avg Margin</th>
                          <th className="text-right py-3 px-4">Dispatch %</th>
                          <th className="text-right py-3 px-4">Refund %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kpiData.daily_metrics.slice(-10).map((day, i) => (
                          <tr key={i} className="border-b border-white/5 hover:bg-muted/20">
                            <td className="py-3 px-4 font-mono">{day.date}</td>
                            <td className="text-right py-3 px-4">{day.total_quotes}</td>
                            <td className="text-right py-3 px-4">{day.total_deposits}</td>
                            <td className="text-right py-3 px-4">
                              <Badge className={day.deposit_conversion_rate >= 20 ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}>
                                {day.deposit_conversion_rate}%
                              </Badge>
                            </td>
                            <td className="text-right py-3 px-4">${day.avg_margin.toFixed(0)}</td>
                            <td className="text-right py-3 px-4">{day.dispatch_success_rate}%</td>
                            <td className="text-right py-3 px-4">
                              <Badge className={day.refund_rate <= 5 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}>
                                {day.refund_rate}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-20 text-muted-foreground">
            Failed to load KPI data
          </div>
        )}
      </div>
  );
}
